import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Save, RotateCcw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { FEE_FIELDS } from "@/lib/calc";
import { useFeeMaster } from "@/lib/hooks";

const slug = (g) => g.replace(/\s+/g, "-").toLowerCase();

export default function FeeMaster() {
  const qc = useQueryClient();
  const { data } = useFeeMaster();
  const [grid, setGrid] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (data) setGrid(structuredClone(data.grades)); }, [data]);

  const setCell = (g, k, val) => setGrid((p) => ({ ...p, [g]: { ...p[g], [k]: val === "" ? 0 : Number(val) } }));

  const run = async (fn, msg) => {
    setBusy(true);
    try {
      const r = await fn();
      qc.setQueryData(["fee-master"], r.data);
      toast.success(msg);
    } catch { toast.error("Failed"); }
    setBusy(false);
  };

  if (!grid) return null;
  let lastGroup = null;
  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div className="fade-up">
          <div className="text-xs uppercase tracking-[0.2em] text-plum-700">Main Sheet · {data.order.length} grades</div>
          <h1 className="font-display mt-0.5 text-3xl font-bold leading-tight tracking-tight text-plum-950 sm:text-4xl">Fee Master</h1>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" disabled={busy} onClick={() => run(() => api.post("/fee-master/reset"), "Reset to workbook defaults")} data-testid="fee-master-reset-btn">
            <RotateCcw className="mr-2 h-4 w-4" />Reset to workbook
          </Button>
          <Button disabled={busy} onClick={() => run(() => api.put("/fee-master", { grades: grid }), "Fee master saved")} className="bg-plum-700 hover:bg-plum-800" data-testid="fee-master-save-btn">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save Fee Master
          </Button>
        </div>
      </div>

      <div className="fade-up d1 overflow-auto rounded-lg border border-plum-900/10 bg-white" style={{ maxHeight: "calc(100vh - 220px)" }}>
        <table className="text-sm" data-testid="fee-master-table">
          <thead className="sticky top-0 z-20">
            <tr className="bg-plum-900 text-white">
              <th className="sticky left-0 z-30 min-w-[160px] bg-plum-900 sm:min-w-[280px] px-4 py-3 text-left text-[11px] uppercase tracking-wider">Fee head</th>
              {data.order.map((g) => <th key={g} className="min-w-[104px] px-2 py-3 text-right text-[11px] font-medium uppercase tracking-wider">{g}</th>)}
            </tr>
          </thead>
          <tbody>
            {FEE_FIELDS.flatMap((f) => {
              const out = [];
              if (f.group !== lastGroup) {
                lastGroup = f.group;
                out.push(
                  <tr key={f.group} className="bg-plum-50">
                    <td className="sticky left-0 z-10 bg-plum-50 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-plum-800" colSpan={1}>{f.group}</td>
                    <td colSpan={data.order.length} />
                  </tr>
                );
              }
              out.push(
                <tr key={f.key} className="border-b border-slate-100">
                  <td className="sticky left-0 z-10 bg-white px-4 py-1.5 text-slate-700">{f.label}</td>
                  {data.order.map((g) => (
                    <td key={g} className="px-1 py-1">
                      <input
                        type="number"
                        value={grid[g][f.key]}
                        onChange={(e) => setCell(g, f.key, e.target.value)}
                        data-testid={`fm-${f.key}-${slug(g)}`}
                        className="font-num w-full rounded border border-transparent bg-slate-50 px-2 py-1.5 text-right text-[13px] outline-none transition-colors duration-150 hover:border-slate-300 focus:border-plum-600 focus:bg-white"
                      />
                    </td>
                  ))}
                </tr>
              );
              return out;
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-3 text-xs text-slate-500">Existing sibling admission discount = (Admission Fee × Adm. Discount %) × Existing Sibling %. Quarterly instalments = (Tuition + Term + CASP) ÷ 4, with the tuition discount deducted from Instalment 4.</p>
    </div>
  );
}
